import { useEffect, useState, useMemo } from "react";
import { InfantReviewChip } from "@/components/InfantReviewChip";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, CheckCircle2, AlertTriangle, Search, Loader2, Wrench, Printer, ChevronDown, ChevronRight, UserX, UserCheck, Baby, Smile, GraduationCap, Backpack, Archive, FolderOpen, FileText, Stethoscope } from "lucide-react";
import AddChildEnrollmentForm from "@/components/AddChildEnrollmentForm";
import { fmtDate } from "@/lib/dates";
import { formatChildName } from "@/lib/names";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const CLASSROOM_GROUPS = [
  { id: "infant",     label: "Infants",     Icon: Baby,          hint: "Under 18 months",        keys: ["infant"] },
  { id: "toddler",    label: "Toddlers",    Icon: Smile,         hint: "18 months – 3 years",    keys: ["toddler", "toddler_young", "toddler_older"] },
  { id: "preschool",  label: "Preschool",   Icon: GraduationCap, hint: "3 – 5 years",            keys: ["preschool", "preschool_25_3", "preschool_3_4", "preschool_4_5"] },
  { id: "school_age", label: "School-age",  Icon: Backpack,      hint: "5 – 11 years",           keys: ["school_age"] },
];

const CLASSROOM_OPTIONS = [
  { id: "infant",     label: "Infants",     hint: "Includes DCY 01234 + DCY 01218" },
  { id: "toddler",    label: "Toddler",     hint: "Includes DCY 01234" },
  { id: "preschool",  label: "Preschool",   hint: "Includes DCY 01234" },
  { id: "school_age", label: "School-age",  hint: "Includes DCY 01234" },
  { id: "other",      label: "Other / Unmapped", hint: "Children with non-standard age groups" },
  { id: "all",        label: "All classrooms", hint: "Every enrolled child" },
];

function ageGroupToBucket(id) {
  for (const g of CLASSROOM_GROUPS) {
    if (g.keys.includes(id)) return g.id;
  }
  return "other";
}

async function downloadClassroomPacket(classroomId, classroomLabel, setBusy) {
  setBusy(true);
  try {
    const res = await api.get("/children-bulk/classroom-packet.pdf", {
      params: { classroom: classroomId },
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${classroomLabel.replace(/\s+/g, "-")}-enrollment-packet-${new Date().toISOString().slice(0, 10)}.pdf`;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    toast.success(`${classroomLabel} packet downloaded.`);
  } catch (err) {
    let detail = formatApiError(err);
    if (err?.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        detail = JSON.parse(text)?.detail || detail;
      } catch { /* keep original */ }
    }
    toast.error(detail);
  } finally {
    setBusy(false);
  }
}

export default function Children() {
  const [list, setList] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [packetBusy, setPacketBusy] = useState(false);
  const [classroomCounts, setClassroomCounts] = useState({});
  const [tab, setTab] = useState("current"); // "current" | "archive"
  const [collapsed, setCollapsed] = useState({}); // {bucketId: true}
  const [deactivateTarget, setDeactivateTarget] = useState(null); // child obj or null
  const [deactivateReason, setDeactivateReason] = useState("");

  // Always load both active + inactive in a single payload so users can flip
  // between tabs without an extra round-trip.
  const load = () => {
    setLoading(true);
    api.get("/children?include_inactive=true").then((r) => setList(r.data)).finally(() => setLoading(false));
    api.get("/children-bulk/classroom-counts")
      .then((r) => setClassroomCounts(r.data?.counts || {}))
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const activeList = useMemo(() => list.filter((c) => c.is_active !== false), [list]);
  const inactiveList = useMemo(() => list.filter((c) => c.is_active === false), [list]);

  const filtered = useMemo(() => activeList.filter((c) =>
    `${c.first_name} ${c.last_name} ${c.parent_name}`.toLowerCase().includes(query.toLowerCase())
  ), [activeList, query]);

  // Group active children by classroom bucket
  const grouped = useMemo(() => {
    const out = {};
    for (const g of CLASSROOM_GROUPS) out[g.id] = [];
    out.other = [];
    for (const c of filtered) {
      const bucket = ageGroupToBucket(c.age_group);
      (out[bucket] || out.other).push(c);
    }
    for (const k of Object.keys(out)) {
      out[k].sort((a, b) => {
        const al = (a.last_name || "").toLowerCase();
        const bl = (b.last_name || "").toLowerCase();
        if (al !== bl) return al < bl ? -1 : 1;
        const af = (a.first_name || "").toLowerCase();
        const bf = (b.first_name || "").toLowerCase();
        return af < bf ? -1 : af > bf ? 1 : 0;
      });
    }
    return out;
  }, [filtered]);

  // Group inactive (archive) children by deactivation year (or "Older" if unknown).
  const archiveByYear = useMemo(() => {
    const filteredArchive = inactiveList.filter((c) =>
      `${c.first_name} ${c.last_name} ${c.parent_name}`.toLowerCase().includes(query.toLowerCase())
    );
    const groups = {};
    for (const c of filteredArchive) {
      const yr = (c.deactivated_at || "").slice(0, 4) || "Older records";
      if (!groups[yr]) groups[yr] = [];
      groups[yr].push(c);
    }
    // Sort each year DESC by deactivation date (most recent first)
    for (const yr of Object.keys(groups)) {
      groups[yr].sort((a, b) => (b.deactivated_at || "").localeCompare(a.deactivated_at || ""));
    }
    const years = Object.keys(groups).sort((a, b) => b.localeCompare(a));
    return { years, groups };
  }, [inactiveList, query]);

  const toggleCollapsed = (bucketId) => setCollapsed((p) => ({ ...p, [bucketId]: !p[bucketId] }));

  const activate = async (child) => {
    try {
      await api.patch(`/children/${child.id}/status`, { is_active: true });
      toast.success(`${formatChildName(child)} reactivated.`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const confirmDeactivate = async () => {
    if (!deactivateTarget) return;
    try {
      await api.patch(`/children/${deactivateTarget.id}/status`, {
        is_active: false,
        reason: deactivateReason.trim() || null,
      });
      toast.success(`${formatChildName(deactivateTarget)} deactivated.`);
      setDeactivateTarget(null);
      setDeactivateReason("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const activeCount = activeList.length;
  const inactiveCount = inactiveList.length;

  return (
    <div data-testid="children-page" className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Enrollment</div>
          <h1 className="mt-2 font-display text-4xl font-medium tracking-tight text-stone-900">Children</h1>
          <p className="mt-2 text-stone-500">
            <strong className="text-emerald-700">{activeCount}</strong> active
            {inactiveCount > 0 && <span className="text-stone-500"> · <strong className="text-stone-700">{inactiveCount}</strong> inactive</span>}
            <span className="text-stone-400"> · grouped by classroom</span>
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input data-testid="children-search" placeholder="Search…" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 w-64 h-11" />
          </div>
          <Button
            data-testid="children-archive-quick-link"
            variant="outline"
            onClick={() => setTab(tab === "archive" ? "current" : "archive")}
            className="rounded-xl h-11 border-stone-300 text-stone-700"
          >
            <Archive className="w-4 h-4 mr-1.5" />
            {tab === "archive" ? "← Back to current" : `Past enrollees${inactiveCount ? ` (${inactiveCount})` : ""}`}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="children-add-btn" className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
                <Plus className="w-4 h-4 mr-1" /> Add child
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add child & enrollment paperwork</DialogTitle>
                <DialogDescription>Enter the child, the family, and the e-signature in one pass. DCY 01234 is filed automatically.</DialogDescription>
              </DialogHeader>
              <AddChildEnrollmentForm onCreated={() => { setOpen(false); load(); }} />
            </DialogContent>
          </Dialog>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="children-print-packet-btn" variant="outline" className="rounded-xl h-11 border-emerald-300 text-emerald-800 hover:bg-emerald-50" disabled={packetBusy}>
                {packetBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}
                Print classroom packet
                <ChevronDown className="w-3.5 h-3.5 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel>Choose a classroom</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {CLASSROOM_OPTIONS.map((opt) => {
                const count = classroomCounts[opt.id] ?? 0;
                const disabled = count === 0 && opt.id !== "all";
                return (
                  <DropdownMenuItem
                    key={opt.id}
                    data-testid={`classroom-packet-${opt.id}`}
                    disabled={disabled}
                    onClick={() => downloadClassroomPacket(opt.id, opt.label, setPacketBusy)}
                    className="flex items-start gap-2"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-stone-900">{opt.label} <span className="text-[10px] font-bold text-stone-500">({count})</span></div>
                      <div className="text-[11px] text-stone-500">{opt.hint}</div>
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            data-testid="children-send-med-reminders-btn"
            variant="outline"
            className="rounded-xl h-11 border-amber-300 text-amber-800 hover:bg-amber-50"
            title="Email parents whose child's medical exam expires within 14 days (or already expired). Idempotent — each parent gets at most one email per expiration date."
            onClick={async () => {
              try {
                const r = await api.post("/children-bulk/medical-expirations/send-reminders");
                const n = r?.data?.sent ?? 0;
                if (n > 0) toast.success(`Emailed ${n} parent${n === 1 ? "" : "s"} about upcoming medical exam expirations.`);
                else toast.info("No reminders needed — all medical exams are >14 days out or already nudged.");
              } catch (err) { toast.error(formatApiError(err)); }
            }}
          >
            <Stethoscope className="w-4 h-4 mr-1" /> Send reminders now
          </Button>
          <Button
            data-testid="children-print-meds-btn"
            variant="outline"
            className="rounded-xl h-11 border-stone-300 text-stone-700"
            title="Print full-name + medical exam expiration list (infants, toddlers, preschool only)"
            onClick={async () => {
              try {
                const res = await api.get("/children-bulk/medical-expirations.pdf", { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `medical-expirations-${new Date().toISOString().slice(0, 10)}.pdf`;
                a.target = "_blank";
                a.rel = "noopener";
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
              } catch (err) {
                // If we hit a 404, that almost always means the browser is serving
                // a stale JS bundle from before the latest deploy. Give the user
                // an actionable hint instead of the raw status code.
                if (err?.response?.status === 404) {
                  toast.error("Looks like your browser is using an old version. Please hard-refresh the page (Ctrl+Shift+R / Cmd+Shift+R) and try again.");
                } else {
                  toast.error(formatApiError(err));
                }
              }
            }}
          >
            <Stethoscope className="w-4 h-4 mr-1" /> Print medical expirations
          </Button>
          <Button
            data-testid="children-repair-parent-names-btn"
            variant="outline"
            className="rounded-xl h-11 border-amber-300 text-amber-800 hover:bg-amber-50"
            title="One-click fix: copies full Parent #1 name from each enrollment back over any single-letter parent_name records."
            onClick={async () => {
              try {
                const r = await api.post("/children/repair-parent-names");
                const n = r?.data?.repaired ?? 0;
                if (n > 0) toast.success(`Repaired ${n} record${n === 1 ? "" : "s"} — refresh to see full parent names.`);
                else toast.info("No records needed repair.");
                load();
              } catch (err) { toast.error(formatApiError(err)); }
            }}
          >
            <Wrench className="w-4 h-4 mr-1" /> Repair parent names
          </Button>
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-stone-100 p-1 rounded-xl h-auto">
          <TabsTrigger value="current" data-testid="tab-current" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-4 py-2.5 font-medium">
            <Smile className="w-3.5 h-3.5 mr-1.5" /> Current children <span className="ml-2 text-[10px] font-bold text-stone-500">{activeCount}</span>
          </TabsTrigger>
          <TabsTrigger value="archive" data-testid="tab-archive" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-4 py-2.5 font-medium">
            <Archive className="w-3.5 h-3.5 mr-1.5" /> Past enrollees <span className="ml-2 text-[10px] font-bold text-stone-500">{inactiveCount}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="current" className="mt-6">
          {loading ? (
            <div className="py-20 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-14 text-center">
              <p className="font-display text-xl text-stone-900">No children yet.</p>
              <p className="mt-2 text-sm text-stone-500">Add your first child — it takes under a minute.</p>
            </div>
          ) : (
            <CurrentClassroomList
              grouped={grouped}
              collapsed={collapsed}
              toggleCollapsed={toggleCollapsed}
              onDeactivate={(c) => { setDeactivateTarget(c); setDeactivateReason(""); }}
            />
          )}
        </TabsContent>

        <TabsContent value="archive" className="mt-6">
          {loading ? (
            <div className="py-20 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>
          ) : (
            <ArchiveView
              archiveByYear={archiveByYear}
              onReactivate={activate}
              isEmpty={inactiveCount === 0}
              hasQuery={!!query.trim()}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Deactivate confirm dialog */}
      <Dialog open={!!deactivateTarget} onOpenChange={(o) => { if (!o) setDeactivateTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="deactivate-dialog">
          <DialogHeader>
            <DialogTitle>Deactivate {deactivateTarget ? formatChildName(deactivateTarget) : ""}?</DialogTitle>
            <DialogDescription>
              They'll be hidden from ratios, attendance, and billing. All forms stay archived — reactivate any time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-xs uppercase tracking-widest font-semibold text-stone-500">Reason (optional)</label>
            <select
              data-testid="deactivate-reason-select"
              value={deactivateReason}
              onChange={(e) => setDeactivateReason(e.target.value)}
              className="w-full border border-stone-300 rounded-xl h-11 px-3 text-sm"
            >
              <option value="">— Select a reason —</option>
              <option>Moved to another center</option>
              <option>Aged out</option>
              <option>Family relocated</option>
              <option>Schedule change</option>
              <option>Other</option>
            </select>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeactivateTarget(null)} className="rounded-xl">Cancel</Button>
            <Button data-testid="deactivate-confirm-btn" onClick={confirmDeactivate} className="bg-stone-800 hover:bg-stone-900 text-white rounded-xl">
              <UserX className="w-4 h-4 mr-1.5" /> Deactivate enrollment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// --- Current children list (grouped by classroom) -----------------------
function CurrentClassroomList({ grouped, collapsed, toggleCollapsed, onDeactivate }) {
  return (
    <div className="space-y-5">
      {CLASSROOM_GROUPS.map((g) => {
        const kids = grouped[g.id] || [];
        if (kids.length === 0) return null;
        const isCollapsed = collapsed[g.id];
        const { Icon } = g;
        return (
          <section key={g.id} data-testid={`classroom-section-${g.id}`} className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
            <button
              data-testid={`classroom-section-toggle-${g.id}`}
              onClick={() => toggleCollapsed(g.id)}
              className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-stone-50 transition-colors"
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center">
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg font-medium text-stone-900">
                  {g.label} <span className="ml-2 text-xs font-bold text-stone-500">{kids.length}</span>
                </div>
                <div className="text-[11px] uppercase tracking-widest text-stone-500">{g.hint}</div>
              </div>
              {isCollapsed ? <ChevronRight className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
            </button>
            {!isCollapsed && (
              <div className="border-t border-stone-100 divide-y divide-stone-100">
                {kids.map((c) => (
                  <div key={c.id} data-testid={`child-row-${c.id}`} className="px-5 py-4 flex flex-wrap items-center gap-4 hover:bg-stone-50">
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-medium text-stone-900">{formatChildName(c)}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <MedExamBadge child={c} />
                        <InfantReviewChip child={c} lastUpdated={c.infant_form_last_updated} testid={`infant-review-due-${c.id}`} />
                        <span className="text-[11px] text-stone-500">
                          Born {fmtDate(c.date_of_birth)}{c.room ? ` · ${c.room}` : ""}
                        </span>
                      </div>
                    </div>
                    <div className="text-sm text-stone-700 min-w-[180px]">
                      <div className="font-medium">{c.parent_name}</div>
                      <div className="text-[11px] text-stone-500">{c.parent_email}</div>
                    </div>
                    <div>
                      {c.jfs_01234_signed ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Signed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-700">
                          <AlertTriangle className="w-3.5 h-3.5" /> Needs e-sign
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <DownloadPacketButton child={c} />
                      <Button
                        data-testid={`child-deactivate-${c.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => onDeactivate(c)}
                        className="rounded-lg h-8 text-xs border-stone-300 text-stone-700 hover:bg-stone-100"
                        title="Mark inactive — hides from ratios, attendance, and billing but keeps all forms."
                      >
                        <UserX className="w-3.5 h-3.5 mr-1" /> Deactivate
                      </Button>
                      <Link
                        to={`/dashboard/children/${c.id}`}
                        data-testid={`child-open-${c.id}`}
                        className="text-sm text-emerald-700 font-semibold hover:underline"
                      >
                        Open →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}
      {(grouped.other && grouped.other.length > 0) && (
        <section data-testid="classroom-section-other" className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <div className="px-5 py-4 bg-amber-50 border-b border-amber-200">
            <div className="font-display text-base font-medium text-stone-900">Other / Unmapped <span className="ml-2 text-xs font-bold text-stone-500">{grouped.other.length}</span></div>
            <div className="text-[11px] text-stone-600">Children with non-standard age groups — open and assign a classroom.</div>
          </div>
          <div className="divide-y divide-stone-100">
            {grouped.other.map((c) => (
              <div key={c.id} className="px-5 py-3 flex items-center justify-between">
                <div className="font-medium text-stone-900">{formatChildName(c)}</div>
                <Link to={`/dashboard/children/${c.id}`} className="text-sm text-emerald-700 font-semibold hover:underline">Open →</Link>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// --- Archive view (Past enrollees grouped by year) -----------------------
function ArchiveView({ archiveByYear, onReactivate, isEmpty, hasQuery }) {
  if (isEmpty) {
    return (
      <div data-testid="archive-empty" className="rounded-2xl border border-dashed border-stone-300 bg-white p-14 text-center">
        <Archive className="w-8 h-8 mx-auto text-stone-300" />
        <p className="mt-3 font-display text-xl text-stone-900">No past enrollees yet.</p>
        <p className="mt-2 text-sm text-stone-500 max-w-md mx-auto">
          When a family leaves, deactivate them from the Current tab. All their signed forms (DCY 01234, medical, etc.) stay archived here so you can pull them up during a state inspection.
        </p>
      </div>
    );
  }
  if (archiveByYear.years.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-14 text-center">
        <p className="font-display text-xl text-stone-900">No matches.</p>
        <p className="mt-2 text-sm text-stone-500">{hasQuery ? "Try a different search term." : "Nothing to display."}</p>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 text-xs text-amber-900 flex items-start gap-2">
        <FolderOpen className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Audit-ready archive.</strong> All signed enrollment paperwork stays here forever — search by family name and click <em>Open archive</em> to see every form on file.
        </div>
      </div>
      {archiveByYear.years.map((yr) => (
        <section key={yr} data-testid={`archive-year-${yr}`} className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-stone-900 text-white grid place-items-center">
                <Archive className="w-4 h-4" />
              </div>
              <div>
                <div className="font-display text-lg font-medium text-stone-900">{yr}</div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500">{archiveByYear.groups[yr].length} former enrollee{archiveByYear.groups[yr].length === 1 ? "" : "s"}</div>
              </div>
            </div>
          </div>
          <div className="divide-y divide-stone-100">
            {archiveByYear.groups[yr].map((c) => {
              const deact = c.deactivated_at ? c.deactivated_at.slice(0, 10) : null;
              const enrolledOn = c.created_at ? new Date(c.created_at).toISOString().slice(0, 10) : null;
              return (
                <div key={c.id} data-testid={`archive-row-${c.id}`} className="px-5 py-4 flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-stone-900">{formatChildName(c)}</span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 border border-stone-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-stone-600">
                        Archived
                      </span>
                    </div>
                    <div className="text-[11px] text-stone-500 mt-0.5">
                      {enrolledOn && deact ? (
                        <>Enrolled <strong>{fmtDate(enrolledOn)}</strong> → left <strong>{fmtDate(deact)}</strong></>
                      ) : deact ? (
                        <>Left {fmtDate(deact)}</>
                      ) : (
                        <>No dates on file</>
                      )}
                      {c.inactive_reason ? <span className="ml-2 italic">· {c.inactive_reason}</span> : null}
                    </div>
                  </div>
                  <div className="text-sm text-stone-700 min-w-[180px]">
                    <div className="font-medium">{c.parent_name}</div>
                    <div className="text-[11px] text-stone-500">{c.parent_email}</div>
                  </div>
                  <div>
                    {c.jfs_01234_signed ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
                        <FileText className="w-3.5 h-3.5" /> DCY 01234 on file
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-500">
                        <AlertTriangle className="w-3.5 h-3.5" /> No e-sign
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <DownloadPacketButton child={c} label="Download packet" testIdPrefix="archive-packet" />
                    <Link
                      to={`/dashboard/children/${c.id}`}
                      data-testid={`archive-open-${c.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 rounded-lg px-3 py-1.5 border border-emerald-200"
                    >
                      <FolderOpen className="w-3.5 h-3.5" /> Open
                    </Link>
                    <Button
                      data-testid={`archive-reactivate-${c.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => onReactivate(c)}
                      className="rounded-lg h-8 text-xs border-stone-300 text-stone-700"
                      title="Reactivate enrollment"
                    >
                      <UserCheck className="w-3.5 h-3.5 mr-1" /> Reactivate
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}


// --- Shared archive packet downloader -----------------------------------
function DownloadPacketButton({ child, label = "Records ZIP", testIdPrefix = "child-packet" }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/children/${child.id}/archive-packet.zip`, { responseType: "blob" });
      const included = res.headers["x-forms-included"];
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(child.last_name || "child")}_${(child.first_name || "")}_archive.zip`;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      toast.success(`Records packet downloaded — ${included || "all"} form(s) included.`);
    } catch (err) {
      let detail = formatApiError(err);
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          detail = JSON.parse(text)?.detail || detail;
        } catch { /* keep original */ }
      }
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      data-testid={`${testIdPrefix}-${child.id}`}
      size="sm"
      disabled={busy}
      onClick={onClick}
      variant="outline"
      className="rounded-lg h-8 text-xs border-stone-300 text-stone-700 hover:bg-stone-100"
      title="Download every signed form on file as a ZIP — perfect for parent record requests or state inspector audits."
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />}
      {busy ? "Building…" : label}
    </Button>
  );
}

// --- Medical exam expiration badge --------------------------------------
// Shows the child's DCY 01305 expiration date inline on each child row.
// Color codes: red (expired/missing), amber (≤30 days), emerald (>30 days).
// School-age kids skip the badge — Ohio rule 5101:2-12-31 doesn't require
// an ongoing medical exam after 5.
function MedExamBadge({ child }) {
  const ageGroup = (child.age_group || "").toLowerCase();
  const isSchoolAge = ageGroup.startsWith("school");
  if (isSchoolAge) return null;
  const exp = child.medical_exam_expires_on;
  if (!exp) {
    return (
      <span
        data-testid={`med-exam-${child.id}`}
        className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-700"
        title="No medical exam on file — needs a fresh DCY 01305."
      >
        <Stethoscope className="w-3 h-3" /> No med exam
      </span>
    );
  }
  const today = new Date();
  const d = new Date(exp);
  const days = Math.floor((d - today) / 86400000);
  let cls = "bg-emerald-50 border-emerald-200 text-emerald-700";
  let label = `Med exp ${fmtDate(exp)}`;
  if (days < 0) {
    cls = "bg-red-50 border-red-300 text-red-700";
    label = `Med EXPIRED ${fmtDate(exp)}`;
  } else if (days <= 30) {
    cls = "bg-amber-50 border-amber-300 text-amber-700";
    label = `Med exp ${fmtDate(exp)} (${days}d)`;
  }
  return (
    <span
      data-testid={`med-exam-${child.id}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}
      title="Medical exam expiration date — DCY 01305"
    >
      <Stethoscope className="w-3 h-3" /> {label}
    </span>
  );
}

